import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import {
  CodeIcon,
  EllipsisIcon,
  MoonIcon,
  RotateCcwIcon,
  SettingsIcon,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type JSX,
  type ReactNode,
  type SetStateAction,
} from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Card } from '@/components/ui/card.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { Switch } from '@/components/ui/switch.js';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs.js';
import { ActionsPanel } from '@/features/developers/ActionsPanel.js';
import { ActivityLog } from '@/features/developers/ActivityLog.js';
import {
  ClearErrorButton,
  DeveloperErrorReason,
} from '@/features/developers/ClearErrorButton.js';
import { ConfirmDialog } from '@/features/developers/ConfirmDialog.js';
import { ConnectionStatusButton } from '@/features/developers/ConnectionStatusButton.js';
import { DeveloperActivityFeed } from '@/features/developers/DeveloperActivityFeed.js';
import { DeveloperChat } from '@/features/developers/DeveloperChat.js';
import { DiskTab } from '@/features/developers/DiskTab.js';
import { ProjectMemoryCard } from '@/features/developers/ProjectMemoryCard.js';
import { ProseTab } from '@/features/developers/ProseTab.js';
import {
  InstructionsTab,
  type InstructionDrafts,
} from '@/features/developers/InstructionsTab.js';
import { ScheduleTab } from '@/features/developers/ScheduleTab.js';
import { SessionsTab } from '@/features/developers/SessionsTab.js';
import { StepsTab, type FlowDrafts } from '@/features/developers/StepsTab.js';
import { DeveloperStatusBadge } from '@/features/developers/DeveloperStatusBadge.js';
import { SpendingDashboard } from '@/features/developers/SpendingDashboard.js';
import { ProviderStatusCard } from '@/features/developers/ProviderStatusCard.js';
import { formatDateTime } from '@/features/developers/date-format.js';
import { developerTypeOf } from '@/features/developers/developer-form.js';
import {
  DEVELOPER_TABS,
  parseDeveloperSearch,
  type DeveloperTab,
} from '@/features/developers/developer-tabs.js';
import { ensureDeveloperEnabled } from '@/features/developers/guard.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import { PageContainer } from '@/ui/PageContainer.js';
import {
  DeveloperNotFoundError,
  developerQueryOptions,
  spendingQueryOptions,
  useDeveloperStatusStream,
  useResetDeveloper,
  useSetDeveloperIdle,
  useRunSuggestion,
  useSuggestions,
  useUpdateDeveloper,
  type Developer,
} from '@/lib/api/developers.js';
import { workspaceQueryOptions } from '@/lib/api/workspaces.js';
import { notify } from '@/lib/notify.js';

export const Route = createFileRoute('/developer')({
  component: DeveloperPage,
  beforeLoad: () => ensureDeveloperEnabled(),
  validateSearch: parseDeveloperSearch,
});

/**
 * `/developer` — operational view for the selected workspace's developer (`developer.id ===
 * workspace.id`). Scoped by the sidebar workspace switcher (same pattern as `/quality`). Config
 * lives in workspace manage → Developer. There is no Delete — the row is removed only when the
 * workspace is deleted; the domain toggle turns Developer off without destroying data.
 */
function DeveloperPage(): JSX.Element {
  const selectedWorkspaceId = useUiStore((s) => s.selectedWorkspaceId);
  useActivityOnWorkspaceSwitch(selectedWorkspaceId);
  const workspaceQuery = useQuery({
    ...workspaceQueryOptions(selectedWorkspaceId ?? ''),
    enabled: selectedWorkspaceId !== null,
  });
  const developerEnabled =
    workspaceQuery.data?.domains?.find(
      (domain) => domain.domain === 'developer',
    )?.enabled ?? false;

  const devQuery = useQuery({
    ...developerQueryOptions(selectedWorkspaceId ?? undefined),
    enabled: selectedWorkspaceId !== null && developerEnabled,
  });

  if (selectedWorkspaceId === null) {
    return (
      <DeveloperStatusShell>
        <StatusCard
          title="No workspace selected"
          body="Pick a workspace in the sidebar to open its Developer."
        />
      </DeveloperStatusShell>
    );
  }

  if (workspaceQuery.isLoading) {
    return (
      <DeveloperStatusShell>
        <Skeleton className="h-40 w-full rounded-xl" />
      </DeveloperStatusShell>
    );
  }

  if (!developerEnabled) {
    return (
      <DeveloperStatusShell>
        <StatusCard
          title="Developer is off for this workspace"
          body="Turn Developer on in the workspace settings to set it up and run it here."
          action={
            <DeveloperManageSettingsLink
              workspaceId={selectedWorkspaceId}
              asButton
            />
          }
        />
      </DeveloperStatusShell>
    );
  }

  if (devQuery.isPending) {
    return (
      <PageContainer maxWidth="full" className="lg:px-8">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-4 h-72 w-full rounded-xl" />
      </PageContainer>
    );
  }

  /**
   * Only when nothing has loaded. The developer polls, so one failed background poll must not blank a
   * good page; a 404 falls through to the not-found card below.
   */
  if (
    devQuery.isError &&
    devQuery.data === undefined &&
    !(devQuery.error instanceof DeveloperNotFoundError)
  ) {
    return (
      <DeveloperStatusShell>
        <StatusCard
          title="Couldn’t load the developer"
          body={devQuery.error.message || 'Try again in a moment.'}
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => void devQuery.refetch()}
            >
              <RotateCcwIcon className="size-4" />
              Retry
            </Button>
          }
        />
      </DeveloperStatusShell>
    );
  }

  if (!devQuery.data) {
    return (
      <DeveloperStatusShell>
        <StatusCard
          title="Developer not found for this workspace"
          body="Enable Developer in workspace settings, or wait for provisioning."
          action={
            <DeveloperManageSettingsLink
              workspaceId={selectedWorkspaceId}
              asButton
            />
          }
        />
      </DeveloperStatusShell>
    );
  }

  return <DeveloperDetail developer={devQuery.data} />;
}

/**
 * Opens Activity when the sidebar moves to another workspace, as a fresh page would. A workspace that
 * only arrives, as the stored choice loads, keeps the tab a deep link asked for.
 */
function useActivityOnWorkspaceSwitch(workspaceId: string | null): void {
  const navigate = useNavigate();
  const shownWorkspaceId = useRef(workspaceId);
  useEffect(() => {
    const previous = shownWorkspaceId.current;
    shownWorkspaceId.current = workspaceId;
    if (previous === null || previous === workspaceId) return;
    void navigate({ to: '/developer', search: {}, replace: true });
  }, [workspaceId, navigate]);
}

function DeveloperStatusShell({
  children,
}: {
  children: JSX.Element;
}): JSX.Element {
  return (
    <PageContainer maxWidth="full" className="gap-5 lg:px-8">
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-xl font-bold tracking-tight">
          Developer
        </h1>
        <p className="text-sm text-muted-foreground">
          Digital Developers watch Jira & GitHub for work addressed to them,
          investigate, and reply.
        </p>
      </div>
      {children}
    </PageContainer>
  );
}

function StatusCard({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: JSX.Element;
}): JSX.Element {
  return (
    <Card className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <span className="flex size-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
        <CodeIcon className="size-6" />
      </span>
      <div>
        <p className="font-medium">{title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      </div>
      {action}
    </Card>
  );
}

function DeveloperDetail({ developer }: { developer: Developer }): JSX.Element {
  const update = useUpdateDeveloper();
  const reset = useResetDeveloper();
  const setIdle = useSetDeveloperIdle();
  const [resetOpen, setResetOpen] = useState(false);
  const [idleOpen, setIdleOpen] = useState(false);
  /** Bumped on reset so the ActivityLog remounts and re-subscribes to the cleared stream. */
  const [resetNonce, setResetNonce] = useState(0);
  /** In the URL, so a link opens the same tab, and "Start chat" in Activity can switch to Chat. */
  const { tab = 'activity' } = useSearch({ from: '/developer' });
  const navigate = useNavigate();
  const openTab = useCallback(
    (next: DeveloperTab) =>
      void navigate({ to: '/developer', search: { tab: next } }),
    [navigate],
  );
  const [openConversationId, setOpenConversationId] = useState<
    string | undefined
  >(undefined);
  /**
   * Unsaved Steps, Instructions and Prose edits. Held here, because moving between the Instructions
   * sections or the page's tabs unmounts the editors.
   */
  const [stepDrafts, setStepDrafts] = useState<FlowDrafts>({});
  const [instructionDrafts, setInstructionDrafts] = useState<InstructionDrafts>(
    {},
  );
  const [proseDrafts, setProseDrafts] = useState<InstructionDrafts>({});
  /** Stable so Chat's apply-once effect doesn't re-run on every render of this page. */
  const clearOpenConversation = useCallback(
    () => setOpenConversationId(undefined),
    [],
  );
  /**
   * The developer currently on screen, or null once this page is gone. A late Start-chat success
   * carries the callback captured at click time, so comparing against `developer.id` from that same
   * closure always matches; the guard has to read what is rendered *now*, which only a ref can give
   * it. Null after unmount, because the tab is in the URL and would steer the next developer's page.
   */
  const currentDeveloperIdRef = useRef<string | null>(developer.id);
  useEffect(() => {
    currentDeveloperIdRef.current = developer.id;
    return () => {
      currentDeveloperIdRef.current = null;
    };
  }, [developer.id]);
  /** Optimistic Active/Inactive so the switch flips immediately; cleared once the query catches up. */
  const [enabledOverride, setEnabledOverride] = useState<boolean | null>(null);
  const enabled = enabledOverride ?? developer.enabled;
  /** Push live `status` SSE events into the cache so the status badge updates without waiting on a poll. */
  useDeveloperStatusStream(developer.id);

  /**
   * The sidebar switcher reuses this component across workspaces, so per-developer view state resets.
   * The tab goes back to Activity in `useActivityOnWorkspaceSwitch`.
   */
  useEffect(() => {
    setOpenConversationId(undefined);
    setStepDrafts({});
    setInstructionDrafts({});
    setProseDrafts({});
  }, [developer.id]);

  useEffect(() => {
    if (enabledOverride !== null && developer.enabled === enabledOverride) {
      setEnabledOverride(null);
    }
  }, [developer.enabled, enabledOverride]);

  async function toggleEnabled(next: boolean): Promise<void> {
    setEnabledOverride(next);
    try {
      await update.mutateAsync({ id: developer.id, patch: { enabled: next } });
    } catch (err) {
      setEnabledOverride(null);
      notify.error(err instanceof Error ? err.message : 'Failed to update');
    }
  }

  async function handleReset(): Promise<void> {
    try {
      await reset.mutateAsync(developer.id);
      setResetOpen(false);
      setResetNonce((nonce) => nonce + 1);
      notify.success('Developer reset to a clean state');
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to reset');
    }
  }

  async function handleSetIdle(): Promise<void> {
    try {
      const result = await setIdle.mutateAsync(developer.id);
      setIdleOpen(false);
      notify.success(
        result.stopped > 0
          ? `Closed ${result.stopped} session${result.stopped === 1 ? '' : 's'} — the developer is idle.`
          : 'The developer was already idle.',
      );
    } catch (err) {
      notify.error(
        err instanceof Error ? err.message : 'Failed to set the developer idle',
      );
    }
  }

  return (
    <PageContainer maxWidth="full" className="h-full min-h-0 gap-5 lg:px-8">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 grow basis-64 items-center gap-2">
          <h1 className="min-w-0 truncate font-heading text-xl font-bold tracking-tight">
            {developer.name}
          </h1>
          <DeveloperStatusBadge
            status={developer.status}
            enabled={enabled}
            className="shrink-0"
          />
          <ClearErrorButton
            developerId={developer.id}
            status={developer.status}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2">
            <Switch
              checked={enabled}
              onCheckedChange={(checked) => void toggleEnabled(checked)}
              disabled={update.isPending}
              aria-label="Active"
            />
            <span className="text-sm text-muted-foreground">
              {enabled ? 'Active' : 'Inactive'}
            </span>
          </label>
          <DeveloperManageSettingsLink workspaceId={developer.id} asButton />
          <ConnectionStatusButton
            key={developer.id}
            developerId={developer.id}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <EllipsisIcon className="size-4" />
                More
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={setIdle.isPending || developer.status === 'idle'}
                onSelect={() => setIdleOpen(true)}
              >
                <MoonIcon className="size-4" />
                Set to idle
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                disabled={reset.isPending}
                onSelect={() => setResetOpen(true)}
              >
                <RotateCcwIcon className="size-4" />
                Reset
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <DeveloperErrorReason
        developerId={developer.id}
        status={developer.status}
      />

      <ProviderStatusCard />

      <Tabs
        value={tab}
        onValueChange={(value) => openTab(value as DeveloperTab)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <TabsList className="shrink-0 flex-wrap" aria-label="Developer">
          {DEVELOPER_TABS.map((option) => (
            <TabsTrigger key={option.value} value={option.value}>
              {option.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent
          value="activity"
          className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto"
        >
          <ActionsPanel
            developerId={developer.id}
            onOpenChat={(conversationId) => {
              setOpenConversationId(conversationId);
              openTab('chat');
            }}
          />
          <PageSection title="Working now">
            <SessionsTab key={developer.id} developerId={developer.id} />
          </PageSection>
          <SuggestedActions developerId={developer.id} />
          <ActivityViews
            key={`${developer.id}-${resetNonce}`}
            developerId={developer.id}
            onChatStarted={(conversationId, forDeveloperId) => {
              /** A create that lands after switching workspace must not steer the new developer's page. */
              if (forDeveloperId !== currentDeveloperIdRef.current) return;
              setOpenConversationId(conversationId);
              openTab('chat');
            }}
          />
        </TabsContent>

        <TabsContent
          value="chat"
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          <DeveloperChat
            key={developer.id}
            developerId={developer.id}
            initialConversationId={openConversationId}
            onInitialConversationOpened={clearOpenConversation}
          />
        </TabsContent>

        <TabsContent
          value="schedules"
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto"
        >
          <ScheduleTab key={developer.id} developerId={developer.id} />
        </TabsContent>

        <TabsContent
          value="usage"
          className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto"
        >
          <PageSection title="Spending">
            <SpendingDashboard
              queryOptionsFor={(period) =>
                spendingQueryOptions(developer.id, period)
              }
            />
          </PageSection>
          <PageSection title="Disk">
            <DiskTab key={developer.id} developerId={developer.id} />
          </PageSection>
        </TabsContent>

        <TabsContent
          value="instructions"
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto"
        >
          <InstructionsSections
            key={developer.id}
            developer={developer}
            stepDrafts={stepDrafts}
            setStepDrafts={setStepDrafts}
            instructionDrafts={instructionDrafts}
            setInstructionDrafts={setInstructionDrafts}
            proseDrafts={proseDrafts}
            setProseDrafts={setProseDrafts}
          />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={idleOpen}
        title="Set to idle?"
        description={`Close every open session for “${developer.name}”? It stops working and goes back to idle. Anything it was waiting to post is dropped rather than sent, and the runs stay in Activity.`}
        confirmLabel="Set to idle"
        pending={setIdle.isPending}
        onConfirm={() => void handleSetIdle()}
        onCancel={() => setIdleOpen(false)}
      />

      <ConfirmDialog
        open={resetOpen}
        title="Reset developer?"
        description={`Clear all runs, the activity log, and the record of what “${developer.name}” already checked in Jira and GitHub? Its settings (tokens, repos, mode) are kept. This can’t be undone.`}
        confirmLabel="Reset developer"
        pending={reset.isPending}
        onConfirm={() => void handleReset()}
        onCancel={() => setResetOpen(false)}
      />
    </PageContainer>
  );
}

/** One titled part of a tab, such as Working now or Spending, headed right under the page's h1. */
function PageSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">{title}</h2>
      {children}
    </section>
  );
}

/**
 * The developer's run history, or the live event stream, behind one switch. The feed comes first:
 * it is what a person reads; the live log is for watching a run as it happens.
 */
function ActivityViews({
  developerId,
  onChatStarted,
}: {
  developerId: string;
  onChatStarted: (conversationId: string, developerId: string) => void;
}): JSX.Element {
  return (
    <Tabs defaultValue="feed" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <TabsList aria-label="Activity view">
          <TabsTrigger value="feed">Feed</TabsTrigger>
          <TabsTrigger value="live-log">Live log</TabsTrigger>
        </TabsList>
        <Link
          to="/developer/activity"
          className="text-xs text-primary-text underline underline-offset-2"
        >
          All developers&apos; activity
        </Link>
      </div>
      <TabsContent value="feed" className="mt-0">
        <DeveloperActivityFeed
          developerId={developerId}
          onChatStarted={onChatStarted}
        />
      </TabsContent>
      <TabsContent value="live-log" className="mt-0">
        <ActivityLog developerId={developerId} className="h-[70vh] min-h-72" />
      </TabsContent>
    </Tabs>
  );
}

/**
 * The Instructions sections, one at a time: the developer's steps, its instructions, how its texts
 * read and its project memory.
 */
const INSTRUCTION_SECTIONS = [
  { value: 'steps', label: 'Steps' },
  { value: 'instructions', label: 'Instructions' },
  { value: 'prose', label: 'Prose' },
  { value: 'project-memory', label: 'Project memory' },
] as const;

/**
 * What shapes the developer's runs: its steps, its own instructions, its prose and its project
 * memory. The page holds the unsaved steps, instructions and prose boxes, so they outlive a move to
 * another section or tab.
 */
function InstructionsSections({
  developer,
  stepDrafts,
  setStepDrafts,
  instructionDrafts,
  setInstructionDrafts,
  proseDrafts,
  setProseDrafts,
}: {
  developer: Developer;
  stepDrafts: FlowDrafts;
  setStepDrafts: Dispatch<SetStateAction<FlowDrafts>>;
  instructionDrafts: InstructionDrafts;
  setInstructionDrafts: Dispatch<SetStateAction<InstructionDrafts>>;
  proseDrafts: InstructionDrafts;
  setProseDrafts: Dispatch<SetStateAction<InstructionDrafts>>;
}): JSX.Element {
  return (
    <Tabs defaultValue="steps" className="flex flex-col gap-4">
      <TabsList className="flex-wrap" aria-label="Instructions sections">
        {INSTRUCTION_SECTIONS.map((section) => (
          <TabsTrigger key={section.value} value={section.value}>
            {section.label}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value="steps" className="mt-0">
        <StepsTab
          developerId={developer.id}
          type={developerTypeOf(developer.type)}
          drafts={stepDrafts}
          setDrafts={setStepDrafts}
        />
      </TabsContent>
      <TabsContent value="instructions" className="mt-0">
        <InstructionsTab
          developerId={developer.id}
          drafts={instructionDrafts}
          setDrafts={setInstructionDrafts}
        />
      </TabsContent>
      <TabsContent value="prose" className="mt-0">
        <ProseTab
          developerId={developer.id}
          drafts={proseDrafts}
          setDrafts={setProseDrafts}
        />
      </TabsContent>
      <TabsContent value="project-memory" className="mt-0">
        <ProjectMemoryCard
          developerId={developer.id}
          repos={developer.repos ?? []}
        />
      </TabsContent>
    </Tabs>
  );
}

function DeveloperManageSettingsLink({
  workspaceId,
  asButton = false,
}: {
  workspaceId: string;
  asButton?: boolean;
}): JSX.Element {
  const link = (
    <Link
      to="/workspaces/$id/manage"
      params={{ id: workspaceId }}
      search={{ tab: 'developer' }}
      className={asButton ? undefined : 'text-sm underline'}
    >
      {asButton ? (
        <>
          <SettingsIcon className="size-4" />
          Settings
        </>
      ) : (
        'Open Developer settings'
      )}
    </Link>
  );

  if (asButton) {
    return (
      <Button variant="outline" size="sm" asChild>
        {link}
      </Button>
    );
  }

  return link;
}

const DISMISSED_KEY = 'cti-developer-dismissed-suggestions';

/** The suggestion triggerRefs the user dismissed for this developer (localStorage-backed, per-device). */
function loadDismissed(developerId: string): Set<string> {
  if (typeof localStorage === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(`${DISMISSED_KEY}:${developerId}`);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(
      Array.isArray(parsed)
        ? parsed.filter((ref): ref is string => typeof ref === 'string')
        : [],
    );
  } catch {
    return new Set();
  }
}

/** Persist dismissed suggestion refs; a storage error (private mode / quota) is ignored. */
function saveDismissed(developerId: string, refs: Set<string>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      `${DISMISSED_KEY}:${developerId}`,
      JSON.stringify([...refs]),
    );
  } catch {
    return;
  }
}

/**
 * Suggested runs: recent (≤3 days) comments, @-mentions, and status transitions from *before* this
 * developer was created, so they never auto-ran. Each can be run on demand or dismissed
 * (dismissal is client-side and per-device; a run item drops off once the server sees its run).
 */
function SuggestedActions({
  developerId,
}: {
  developerId: string;
}): JSX.Element | null {
  const { data, isError, error } = useSuggestions(developerId);
  const runSuggestion = useRunSuggestion();
  const [dismissed, setDismissed] = useState<Set<string>>(() =>
    loadDismissed(developerId),
  );

  /** Re-seed from storage when the route is reused for a different developer (no unmount). */
  useEffect(() => {
    setDismissed(loadDismissed(developerId));
  }, [developerId]);

  function dismiss(triggerRef: string): void {
    setDismissed((prev) => {
      const next = new Set(prev).add(triggerRef);
      saveDismissed(developerId, next);
      return next;
    });
  }

  if (isError) {
    return (
      <p className="text-xs text-destructive">
        {error instanceof Error ? error.message : 'Failed to load suggestions.'}
      </p>
    );
  }

  const suggestions = (data ?? []).filter(
    (suggestion) => !dismissed.has(suggestion.triggerRef),
  );
  if (suggestions.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium text-muted-foreground">
        Suggested runs: recent items from before this developer was created
      </p>
      {suggestions.map((suggestion) => (
        <Card key={suggestion.triggerRef} className="flex flex-col gap-1.5 p-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline">{suggestion.kind}</Badge>
            <span className="truncate text-xs text-muted-foreground">
              {suggestion.ref}
            </span>
            {suggestion.at ? (
              <span className="text-xs text-muted-foreground">
                {formatDateTime(suggestion.at)}
              </span>
            ) : null}
          </div>
          <p className="line-clamp-2 text-sm">{suggestion.title}</p>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={runSuggestion.isPending}
              onClick={() =>
                runSuggestion.mutate(
                  { id: developerId, suggestion },
                  {
                    onSuccess: () => {
                      dismiss(suggestion.triggerRef);
                      notify.success(`Queued a run for ${suggestion.ref}`);
                    },
                    onError: (err) =>
                      notify.error(
                        err instanceof Error
                          ? err.message
                          : 'Failed to run the suggestion',
                      ),
                  },
                )
              }
            >
              Run
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => dismiss(suggestion.triggerRef)}
            >
              Dismiss
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}
